import { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import api, { MAPS_KEY } from "../lib/api";
import { MapPin } from "lucide-react";
import ManualLocationPicker from "./ManualLocationPicker";

/* ─────────────────────────────────────────────────────────────────
   Category glyphs (identical to the previous Leaflet build)
   ────────────────────────────────────────────────────────────── */
const CATEGORY_EMOJI = {
  taxi: "🚕", towing: "🚚", mechanic: "🔧", tire: "🛞",
  moving: "📦", delivery: "🛵",
};

/* ─────────────────────────────────────────────────────────────────
   Country-capital fallback framing (unchanged from the previous
   Leaflet build — pure data, no map-library dependency).
   ────────────────────────────────────────────────────────────── */
const COUNTRY_CAPITALS = {
  TR: [41.0082, 28.9784], IL: [32.0853, 34.7818], US: [40.7128, -74.006],
  GB: [51.5074, -0.1278], FR: [48.8566, 2.3522], DE: [52.5200, 13.4050],
  RU: [55.7558, 37.6173], AE: [25.2048, 55.2708], SA: [24.7136, 46.6753],
  EG: [30.0444, 31.2357], IN: [28.6139, 77.2090], BR: [-23.5505, -46.6333],
  CA: [43.6532, -79.3832], AU: [-33.8688, 151.2093], JP: [35.6762, 139.6503],
  NL: [52.3676, 4.9041], ES: [40.4168, -3.7038], IT: [41.9028, 12.4964],
  MX: [19.4326, -99.1332], PL: [52.2297, 21.0122], UA: [50.4501, 30.5234],
  ZA: [-33.9249, 18.4241], AR: [-34.6037, -58.3816], KR: [37.5665, 126.9780],
  CN: [39.9042, 116.4074], ID: [-6.2088, 106.8456], TH: [13.7563, 100.5018],
  SG: [1.3521, 103.8198], MY: [3.1390, 101.6869], PH: [14.5995, 120.9842],
  VN: [21.0285, 105.8542], NG: [6.5244, 3.3792], KE: [-1.2921, 36.8219],
  MA: [33.5731, -7.5898], DZ: [36.7538, 3.0588], TN: [36.8065, 10.1815],
  JO: [31.9454, 35.9284], LB: [33.8938, 35.5018], QA: [25.2854, 51.5310],
  KW: [29.3759, 47.9774], BH: [26.2285, 50.5860], OM: [23.5880, 58.3829],
  IR: [35.6892, 51.3890], IQ: [33.3152, 44.3661], PK: [24.8607, 67.0011],
  BD: [23.8103, 90.4125],
};
function _cachedCountryCapital() {
  try {
    const raw = localStorage.getItem("haulhub_geo");
    if (!raw) return null;
    const g = JSON.parse(raw);
    return COUNTRY_CAPITALS[(g?.country || "").toUpperCase()] || null;
  } catch { return null; }
}

/* ─────────────────────────────────────────────────────────────────
   Tuple <-> LatLngLiteral helpers.
   The public props interface still accepts [lat, lng] tuples (as
   the previous Leaflet build did) so RideRequest.jsx does not need
   any data-shape changes.
   ────────────────────────────────────────────────────────────── */
const asLL = (t) => (t && t.length === 2 ? { lat: t[0], lng: t[1] } : null);

/* ─────────────────────────────────────────────────────────────────
   FitToRoute — programmatically frame the map around the trip path.
   Runs inside APIProvider/Map context so useMap() returns the Google
   map instance. Falls back gracefully if geometry lib isn't ready yet.
   ────────────────────────────────────────────────────────────── */
function FitToRoute({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !points || points.length < 2) return;
    if (!window.google?.maps?.LatLngBounds) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
      map.fitBounds(bounds, 60); // 60px padding on all sides
    } catch (e) { /* ignore */ }
  }, [map, points?.length, points?.[0]?.[0], points?.[0]?.[1],
      points?.[points?.length - 1]?.[0], points?.[points?.length - 1]?.[1]]);
  return null;
}

/* ─────────────────────────────────────────────────────────────────
   PanTo — smoothly recenters the map when the origin changes and
   there is no route to fit.
   ────────────────────────────────────────────────────────────── */
function PanTo({ center, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !enabled || !center) return;
    try { map.panTo({ lat: center[0], lng: center[1] }); }
    catch (e) { /* ignore */ }
  }, [map, enabled, center?.[0], center?.[1]]);
  return null;
}

/* ─────────────────────────────────────────────────────────────────
   RoutePolyline — draws the OSRM road geometry with an outer black
   stroke and inner cyan stroke, exactly like the previous build.
   Uses the imperative google.maps.Polyline API (per Drivons Market
   reference) since @vis.gl does not ship a first-class Polyline
   component.
   ────────────────────────────────────────────────────────────── */
function RoutePolyline({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps?.Polyline) return;
    if (!points || points.length < 2) return;
    const path = points.map(([lat, lng]) => ({ lat, lng }));
    const outer = new window.google.maps.Polyline({
      path, map,
      strokeColor: "#050505",
      strokeOpacity: 0.75,
      strokeWeight: 8,
      zIndex: 1,
    });
    const inner = new window.google.maps.Polyline({
      path, map,
      strokeColor: "#00E5FF",
      strokeOpacity: 1,
      strokeWeight: 4,
      zIndex: 2,
    });
    return () => { outer.setMap(null); inner.setMap(null); };
  }, [map, points]);
  return null;
}

/* ─────────────────────────────────────────────────────────────────
   RadiusCircle — search-radius ring around the customer origin.
   ────────────────────────────────────────────────────────────── */
function RadiusCircle({ center, radiusKm }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps?.Circle || !center) return;
    const circle = new window.google.maps.Circle({
      map,
      center: { lat: center[0], lng: center[1] },
      radius: radiusKm * 1000,
      strokeColor: "#00B14F",
      strokeOpacity: 0.25,
      strokeWeight: 1,
      fillColor: "#00B14F",
      fillOpacity: 0.04,
      clickable: false,
    });
    return () => circle.setMap(null);
  }, [map, center?.[0], center?.[1], radiusKm]);
  return null;
}

/* ─────────────────────────────────────────────────────────────────
   Custom HTML marker glyphs — preserved from the Leaflet build.
   AdvancedMarker renders our JSX as-is, giving pixel-identical
   visuals to the previous L.divIcon markers.
   ────────────────────────────────────────────────────────────── */
const MeGlyph = () => (
  <div style={{ position: "relative", width: 22, height: 22 }}>
    <div style={{
      position: "absolute", inset: 0, borderRadius: 9999,
      background: "#3b82f6", opacity: 0.3,
      animation: "ndm-pulse 1.6s ease-out infinite",
    }} />
    <div style={{
      position: "absolute", inset: 5, borderRadius: 9999,
      background: "#3b82f6", boxShadow: "0 0 0 3px #0a0a0a",
    }} />
  </div>
);

const PickupGlyph = () => (
  <div style={{ position: "relative", width: 20, height: 20 }}>
    <div style={{
      position: "absolute", inset: 0, borderRadius: 4, background: "#ffffff",
      boxShadow: "0 0 0 4px rgba(255,255,255,0.15),0 4px 12px rgba(0,0,0,0.6)",
    }} />
  </div>
);

const DropoffGlyph = () => (
  <div style={{ position: "relative", width: 22, height: 22 }}>
    <div style={{
      position: "absolute", inset: 0, borderRadius: 9999, background: "#00E5FF",
      boxShadow: "0 0 0 4px rgba(0,229,255,0.2),0 4px 12px rgba(0,0,0,0.6)",
    }} />
  </div>
);

const DriverGlyph = ({ category }) => {
  const isCar = category === "taxi" || !category;
  if (isCar) {
    const gid = `ng${category || "d"}`;
    return (
      <div style={{ width: 26, height: 38, position: "relative", filter: "drop-shadow(0 3px 5px rgba(0,229,255,.35))" }}>
        <svg viewBox="0 0 24 40" width="24" height="38" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00E5FF" />
              <stop offset="100%" stopColor="#00A0B4" />
            </linearGradient>
          </defs>
          <rect x="4" y="6" width="16" height="28" rx="5" ry="5" fill={`url(#${gid})`} stroke="#050505" strokeWidth="1.5" />
          <rect x="6" y="10" width="12" height="7" rx="2" fill="#0A0A0A" opacity="0.85" />
          <rect x="6" y="23" width="12" height="7" rx="2" fill="#0A0A0A" opacity="0.85" />
        </svg>
      </div>
    );
  }
  return (
    <div style={{
      width: 34, height: 34, borderRadius: 9999, background: "#FFC800",
      boxShadow: "0 0 0 3px #050505,0 0 12px rgba(255,200,0,.4)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 15,
    }}>
      {CATEGORY_EMOJI[category] || "🚗"}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MapContents — everything inside APIProvider that needs useMap().
   Kept as a subtree so we can conditionally decide between PanTo
   (no route) and FitToRoute (with route) without conflicting camera
   animations.
   ────────────────────────────────────────────────────────────── */
function MapContents({
  origin, pickup, dropoff, routeGeometry, drivers, radiusKm,
  onPickupDrag, category, activeInfo, setActiveInfo,
}) {
  const routePoints = useMemo(() => {
    if (routeGeometry && routeGeometry.length > 1) return routeGeometry;
    if (pickup && dropoff) return [[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]];
    return null;
  }, [routeGeometry, pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng]);

  return (
    <>
      {/* Camera control: fit the whole trip if we have both endpoints,
          otherwise pan to the customer's origin. */}
      {routePoints ? <FitToRoute points={routePoints} /> : <PanTo center={origin} enabled={true} />}

      {/* Route + radius */}
      {routeGeometry && routeGeometry.length > 1 && <RoutePolyline points={routeGeometry} />}
      {origin && <RadiusCircle center={origin} radiusKm={radiusKm} />}

      {/* Pickup — draggable when the parent provided a callback */}
      {pickup && (
        <AdvancedMarker
          position={asLL([pickup.lat, pickup.lng])}
          draggable={!!onPickupDrag}
          gmpDraggable={!!onPickupDrag}
          onDragEnd={(e) => {
            if (!onPickupDrag) return;
            // @vis.gl passes a google.maps.MapMouseEvent-like object
            const lat = e?.latLng?.lat?.() ?? e?.latLng?.lat;
            const lng = e?.latLng?.lng?.() ?? e?.latLng?.lng;
            if (typeof lat === "number" && typeof lng === "number") onPickupDrag({ lat, lng });
          }}
          onClick={() => setActiveInfo({ kind: "pickup", position: { lat: pickup.lat, lng: pickup.lng } })}
        >
          <PickupGlyph />
        </AdvancedMarker>
      )}
      {activeInfo?.kind === "pickup" && (
        <InfoWindow position={activeInfo.position} onCloseClick={() => setActiveInfo(null)}>
          {onPickupDrag ? "Drag me to fine-tune pickup" : "Pickup"}
        </InfoWindow>
      )}

      {/* Dropoff */}
      {dropoff && (
        <AdvancedMarker
          position={asLL([dropoff.lat, dropoff.lng])}
          onClick={() => setActiveInfo({ kind: "dropoff", position: { lat: dropoff.lat, lng: dropoff.lng } })}
        >
          <DropoffGlyph />
        </AdvancedMarker>
      )}
      {activeInfo?.kind === "dropoff" && (
        <InfoWindow position={activeInfo.position} onCloseClick={() => setActiveInfo(null)}>Drop-off</InfoWindow>
      )}

      {/* Customer origin (blue pulse) */}
      {origin && (
        <AdvancedMarker
          position={asLL(origin)}
          onClick={() => setActiveInfo({ kind: "me", position: { lat: origin[0], lng: origin[1] } })}
        >
          <MeGlyph />
        </AdvancedMarker>
      )}
      {activeInfo?.kind === "me" && (
        <InfoWindow position={activeInfo.position} onCloseClick={() => setActiveInfo(null)}>You are here</InfoWindow>
      )}

      {/* Nearby drivers */}
      {drivers.map((d) => (
        <AdvancedMarker
          key={d.supplier_id}
          position={{ lat: d.lat, lng: d.lng }}
          onClick={() => setActiveInfo({
            kind: "driver", supplier_id: d.supplier_id,
            position: { lat: d.lat, lng: d.lng }, data: d,
          })}
        >
          <DriverGlyph category={d.category || category} />
        </AdvancedMarker>
      ))}
      {activeInfo?.kind === "driver" && (
        <InfoWindow position={activeInfo.position} onCloseClick={() => setActiveInfo(null)}>
          <div style={{ fontWeight: 800 }}>{activeInfo.data.name || "Nearby Driver"}</div>
          <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase" }}>
            {activeInfo.data.category} · {activeInfo.data.distance_km} km
          </div>
          {(activeInfo.data.vehicle_color || activeInfo.data.vehicle_model) && (
            <div style={{ fontSize: 11, marginTop: 2 }}>
              {[activeInfo.data.vehicle_color, activeInfo.data.vehicle_model].filter(Boolean).join(" ")}
            </div>
          )}
        </InfoWindow>
      )}
    </>
  );
}

/**
 * NearbyDriversMap — customer-facing map showing online drivers around
 * the rider's origin. Polls `/api/drivers/nearby` every `pollMs`
 * (default 8000 ms). If no `center` is given, falls back to browser
 * GPS → cached country capital → NYC.
 *
 * Rendered with @vis.gl/react-google-maps (matches Drivons Market).
 *
 * PUBLIC PROPS INTERFACE — unchanged from the previous Leaflet build:
 *   center             [lat, lng] | null
 *   pickup             {lat, lng} | null
 *   dropoff            {lat, lng} | null
 *   routeGeometry      [[lat, lng], ...] | null   (OSRM road geometry)
 *   onPickupDrag       (coords) => void           (draggable pickup)
 *   onDriversChange    (drivers[]) => void
 *   category           null | "taxi" | "delivery" | "moving" | ...
 *   taxiType           null | "regular" | "hybrid" | ...
 *   minTruckTonnageBand null | "1t" | "3.5t" | ...
 *   radiusKm           number                     (default 10)
 *   pollMs             number                     (default 8000)
 *   height             number                     (default 260)
 *   fullscreen         boolean                    (default false)
 *   darkTiles          boolean                    (default false, no-op — Google Maps uses mapId theme)
 *   className          string
 */
export default function NearbyDriversMap({
  category = null,
  center = null,
  radiusKm = 10,
  height = 260,
  pollMs = 8000,
  className = "",
  taxiType = null,
  minTruckTonnageBand = null,
  fullscreen = false,
  darkTiles = false, // preserved for prop-compat; Google Maps styling is via mapId
  onDriversChange = null,
  pickup = null,
  dropoff = null,
  routeGeometry = null,
  onPickupDrag = null,
}) {
  const [origin, setOrigin] = useState(center);
  const [drivers, setDrivers] = useState([]);
  const [err, setErr] = useState(null);
  const [gpsDenied, setGpsDenied] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [activeInfo, setActiveInfo] = useState(null);
  const timer = useRef(null);

  // Install gm_authFailure hook once — Google calls this on window if the
  // API key is invalid/restricted so we can render a graceful fallback
  // instead of an empty tile grid.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => setAuthFailed(true);
    return () => { window.gm_authFailure = prev; };
  }, []);

  // Resolve origin: explicit prop → GPS → cached country capital → NYC.
  useEffect(() => {
    if (center && center[0] && center[1]) { setOrigin(center); return; }
    const capital = _cachedCountryCapital();
    const fallback = capital || [40.7128, -74.006];
    if (!navigator.geolocation) { setOrigin(fallback); setGpsDenied(true); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setOrigin([pos.coords.latitude, pos.coords.longitude]); setGpsDenied(false); },
      (geoErr) => {
        setOrigin(fallback);
        if (geoErr && geoErr.code === 1) setGpsDenied(true);
      },
      { timeout: 8000, maximumAge: 60000 }
    );
  }, [center?.[0], center?.[1]]);

  // Poll nearby drivers whenever origin or filters change.
  useEffect(() => {
    if (!origin) return;
    let mounted = true;
    const fetchDrivers = async () => {
      try {
        const { data } = await api.get("/drivers/nearby", {
          params: {
            lat: origin[0], lng: origin[1],
            category: category || undefined,
            radius_km: radiusKm, limit: 50,
            taxi_type: taxiType || undefined,
            min_truck_tonnage_band: minTruckTonnageBand || undefined,
          },
        });
        if (mounted) { setDrivers(data); setErr(null); if (onDriversChange) onDriversChange(data); }
      } catch (e) {
        if (mounted) setErr(e?.response?.data?.detail || "Could not load drivers");
      }
    };
    fetchDrivers();
    timer.current = setInterval(fetchDrivers, pollMs);
    return () => { mounted = false; clearInterval(timer.current); };
  }, [origin?.[0], origin?.[1], category, radiusKm, pollMs, taxiType, minTruckTonnageBand]);

  const count = drivers.length;
  const keyMissing = !MAPS_KEY;

  return (
    <div
      className={`relative overflow-hidden ${fullscreen ? "" : "rounded-2xl border border-ink-border bg-ink-surface2"} ${className}`}
      style={{ height }}
      data-testid="nearby-drivers-map"
    >
      {/* Google Maps stack */}
      {origin && !keyMissing && !authFailed && (
        <APIProvider apiKey={MAPS_KEY} libraries={["geometry"]}>
          <Map
            mapId="drivons-map"
            defaultCenter={{ lat: origin[0], lng: origin[1] }}
            defaultZoom={13}
            gestureHandling="greedy"
            disableDefaultUI={true}
            zoomControl={true}
            style={{ width: "100%", height: "100%" }}
          >
            <MapContents
              origin={origin}
              pickup={pickup}
              dropoff={dropoff}
              routeGeometry={routeGeometry}
              drivers={drivers}
              radiusKm={radiusKm}
              onPickupDrag={onPickupDrag}
              category={category}
              activeInfo={activeInfo}
              setActiveInfo={setActiveInfo}
            />
          </Map>
        </APIProvider>
      )}

      {/* Graceful fallback — missing key or gm_authFailure fired */}
      {(keyMissing || authFailed) && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-ink-surface2 text-center px-4"
          data-testid="nearby-drivers-map-fallback"
        >
          <MapPin size={22} className="text-brand-warn mb-2" />
          <div className="text-[13px] font-black text-white/90">
            {keyMissing ? "Map unavailable" : "Map authentication failed"}
          </div>
          <div className="text-[11px] text-white/60 mt-1 max-w-xs">
            {keyMissing
              ? "The map is being configured. Try again shortly."
              : "Please retry — driver data still updates in the background."}
          </div>
          <div className="mt-2 text-[11px] text-white/70" data-testid="nearby-drivers-fallback-count">
            {count} {count === 1 ? "driver nearby" : "drivers nearby"}
          </div>
        </div>
      )}

      {/* Overlay pill — always visible */}
      <div className="absolute top-2 left-2 pointer-events-none">
        <div className="bg-black/70 backdrop-blur rounded-full px-3 py-1 flex items-center gap-1.5 text-[11px] font-black text-white" data-testid="nearby-drivers-count">
          <MapPin size={11} className="text-brand-accent" />
          {count} {count === 1 ? "driver nearby" : "drivers nearby"}
        </div>
      </div>

      {err && (
        <div className="absolute bottom-2 left-2 right-2 bg-black/70 rounded-xl px-3 py-2 text-[11px] text-brand-warn" data-testid="nearby-drivers-error">
          {err}
        </div>
      )}

      {gpsDenied && (
        <ManualLocationPicker
          onPick={(coords) => { setOrigin(coords); setGpsDenied(false); }}
          onClose={() => setGpsDenied(false)}
        />
      )}

      <style>{`
        @keyframes ndm-pulse { 0% { transform:scale(1); opacity:.4 } 70% { transform:scale(2.2); opacity:0 } 100% { opacity:0 } }
      `}</style>
    </div>
  );
}
